/**
 * FORZAR ACTUALIZACIÓN — «no veo lo que debería ver».
 *
 * ÁGORA es una PWA con service worker (`registerType: 'autoUpdate'`), así que
 * un teléfono puede quedarse con una versión vieja de la app en caché: entra a
 * la sala, y ve una pantalla que ya no corresponde. Recargar a mano no siempre
 * alcanza —el service worker vuelve a servir lo mismo desde su caché—.
 *
 * Esta rutina hace lo único que sí funciona, en orden:
 *   1. Vacía Cache Storage (donde el service worker guarda los assets).
 *   2. Le pide al service worker que busque una versión nueva.
 *   3. Recarga la página, que ya baja todo del servidor.
 *
 * Lo que NO toca: localStorage (ahí vive la cola de aportes de texto y el
 * puntero de sala) ni IndexedDB (ahí viven los audios pendientes). Un botón
 * de "actualizar" que borre lo que la persona todavía no pudo enviar sería
 * exactamente el desastre que estamos evitando.
 */
export async function forzarActualizacion(): Promise<void> {
  // 1) Caché de assets del service worker.
  try {
    if ('caches' in window) {
      const claves = await caches.keys();
      await Promise.all(claves.map((k) => caches.delete(k)));
    }
  } catch {
    /* sin Cache Storage: la recarga igual sirve */
  }

  // 2) Que el service worker busque versión nueva antes de recargar.
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
    }
  } catch {
    /* idem */
  }

  // 3) Recarga limpia. El cache-buster evita que un intermediario devuelva
  //    el index.html viejo con el <script> del bundle anterior.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', Date.now().toString(36));
  window.location.replace(url.toString());
}
